// The fleet as the workspace reads it: the range's sessions by machine, each
// with what the review found in it and where Triage left each finding; the
// triage outcomes, each finding's session; and what a machine and an agent
// are called. Read once per range and shared by every reader of it.
import { useMemo } from "react";
import { useFindingSessions } from "./WorkspaceSidebar.jsx";
import { PROVIDER_TAG_TO_IDENTITY, deriveAgentIdentity } from "../lib/agentIdentity.js";
import { rangeBoundsMs } from "../lib/analyticsClient.js";
import { useQuery } from "../lib/dataClient.js";
import { dimensionMeta, scopeStepLabel, useLabels } from "../lib/dimensionDefs.js";
import { useFleetFindings } from "../lib/fleetFindings.js";
import { useKeyedReads } from "../lib/keyedReads.js";
import { toApiRange } from "../lib/shell.js";

const NONE = [];
const DAY = 24 * 3_600_000;

export const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
export const VERDICT_WORD = { confirmed: "verified true", partial: "partly true", refuted: "not true", unverifiable: "could not be verified", non_adverse: "not adverse" };
export const VERDICT_SHORT = { confirmed: "true", partial: "partly true", refuted: "false", unverifiable: "couldn't check", non_adverse: "not about an action" };
export const ASKABLE = new Set(["confirmed", "partial"]);
// Where Triage left each finding, as the Triage tab's sections name them.
export const OUTCOMES = [
  { id: "met", label: "Standard met", why: "a person should look" },
  { id: "not_met", label: "Standard not met", why: "true or partly true, but no harm to anyone" },
  { id: "not_asked", label: "Not asked yet", why: "true or partly true; no standard has answered" },
  { id: "set_aside", label: "Set aside", why: "false, couldn't check, or not about an action" },
  { id: "not_triaged", label: "Not verified yet", why: "kept, not yet read against its session" },
];

export const agentName = (a) => (a ? PROVIDER_TAG_TO_IDENTITY[a]?.name || deriveAgentIdentity(a)?.name || a : "");
export const worstOf = (findings) => findings.reduce((m, f) => Math.min(m, SEV_RANK[f.severity] ?? 9), 9);
export const titleOf = (s) => s?.title || `Session ${String(s?.id || "").slice(0, 8)}`;
// The sessions that most need a look first: a standard met, then the worst
// found, then the newest.
export const byConcern = (a, b) => (b.met.length ? 1 : 0) - (a.met.length ? 1 : 0) || a.worst - b.worst || (b.firstMs || 0) - (a.firstMs || 0);

// A machine's name and whose it is, from the endpoints' labels.
export function machineLabel(ep) {
  if (ep === "unknown") return { name: "Machine not known", person: "" };
  const name = scopeStepLabel({ dim: "endpoints", id: ep });
  const meta = dimensionMeta("endpoints", ep);
  return { name: name && name !== ep ? name : `machine ${ep.slice(0, 8)}`, person: meta?.registeredUser || "" };
}
export const personName = (id) => {
  const name = scopeStepLabel({ dim: "identities", id });
  return name && name !== id ? name : "Someone in the directory";
};

let modelRev = 0;

// The range's sessions by machine: the newest the session read holds and every
// one the review found something in, each with its findings and what their
// verification and the standards said, and each machine's range day by day.
// `heads`: whether what each session was about is read (a page of sessions
// at a time, from their newest prompts, shared with every list that reads
// the same): only what shows a session by its title needs it (the sheet's
// lists, a workspace's machines), and on a range of a thousand sessions it is
// the model's heaviest read.
export function useFleetModel(range, { heads: wantHeads = true } = {}) {
  const match = useQuery("getSessionMatches", { scopePath: [], range, query: "" });
  const found = useFindingSessions(range);
  const fleet = useFleetFindings(range);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const span = useMemo(() => {
    const { fromMs, toMs } = rangeBoundsMs(toApiRange(range));
    const to = Math.min(toMs, Date.now());
    return { fromMs, toMs: to, days: Math.max(1, Math.min(92, Math.ceil((to - fromMs) / DAY))) };
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const base = useMemo(() => {
    const out = new Map();
    for (const s of match.data?.sessions || NONE) {
      out.set(s.id, { id: s.id, endpointId: s.endpointId || "", agent: s.agent || "", firstMs: s.firstMs, lastMs: s.lastMs, requests: s.requests || 0, summary: "" });
    }
    for (const s of found.sessions || NONE) {
      const cur = out.get(s.id);
      out.set(s.id, {
        id: s.id,
        endpointId: cur?.endpointId || s.endpointId || "",
        agent: cur?.agent || "",
        firstMs: cur?.firstMs ?? s.firstAt ?? null,
        lastMs: cur?.lastMs ?? s.latestAt ?? null,
        requests: cur?.requests || 0,
        summary: s.summary || "",
      });
    }
    return out;
  }, [match.data, found.sessions]);
  // What each session was about, read a page of sessions at a time.
  const untitled = useMemo(() => (wantHeads ? [...base.values()].filter((s) => !s.summary).map((s) => s.id) : NONE), [base, wantHeads]);
  const headsRead = useKeyedReads("getSessionHeads", untitled, { range }, { chunk: 200 });
  const heads = headsRead.data;
  const headsLeft = headsRead.loading;
  const endpoints = useMemo(() => [...new Set([...base.values()].map((s) => s.endpointId).filter(Boolean))], [base]);
  const labelsTick = useLabels(useMemo(() => endpoints.map((id) => ({ dim: "endpoints", id })), [endpoints]));
  const findingsReady = fleet.status !== "loading";
  const model = useMemo(() => {
    const findingsOf = new Map();
    for (const f of fleet.findings || NONE) {
      if (!findingsOf.has(f.session)) findingsOf.set(f.session, []);
      findingsOf.get(f.session).push(f);
    }
    const machines = new Map();
    for (const s of base.values()) {
      const head = heads[s.id] || null;
      const findings = [...(findingsOf.get(s.id) || NONE)].sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9));
      const ep = s.endpointId || head?.endpointId || "unknown";
      const session = {
        ...s,
        endpointId: ep,
        agent: s.agent || head?.provider || "",
        title: s.summary || head?.summary || "",
        findings,
        worst: worstOf(findings),
        met: [...new Set(findings.flatMap((f) => f.met || NONE))],
        verified: findings.filter((f) => ASKABLE.has(f.verdict)).length,
      };
      if (!machines.has(ep)) machines.set(ep, { id: ep, sessions: [] });
      machines.get(ep).sessions.push(session);
    }
    const list = [...machines.values()].map((m) => {
      const flagged = m.sessions.filter((s) => s.findings.length).sort(byConcern);
      const clean = m.sessions.filter((s) => !s.findings.length).sort((a, b) => (b.firstMs || 0) - (a.firstMs || 0));
      // Its range day by day: how many sessions began, and the worst found.
      const days = new Array(span.days).fill(0);
      const dayWorst = new Array(span.days).fill(9);
      for (const s of m.sessions) {
        if (!Number.isFinite(s.firstMs)) continue;
        const d = Math.floor((s.firstMs - span.fromMs) / DAY);
        if (d < 0 || d >= span.days) continue;
        days[d] += 1;
        dayWorst[d] = Math.min(dayWorst[d], s.met.length ? -1 : s.worst);
      }
      return {
        ...m,
        flagged,
        clean,
        days,
        dayWorst,
        met: flagged.filter((s) => s.met.length).length,
        worst: flagged.reduce((w, s) => Math.min(w, s.worst), 9),
      };
    }).sort((a, b) => b.met - a.met || a.worst - b.worst || b.flagged.length - a.flagged.length || b.sessions.length - a.sessions.length);
    const sessions = new Map(list.flatMap((m) => m.sessions.map((s) => [s.id, s])));
    modelRev += 1;
    return { machines: list, byId: new Map(list.map((m) => [m.id, m])), rank: new Map(list.map((m, i) => [m.id, i])), sessions, rev: modelRev };
  }, [base, heads, fleet.findings, span]);
  const reading = [
    match.loading || (!match.data && !match.error) ? "Reading the range's sessions" : null,
    found.status === "loading" ? "Reading the sessions the review flagged" : null,
    fleet.status === "loading" ? "Reading what was found and how it was verified" : null,
    headsLeft ? "Reading what the sessions were about" : null,
  ].filter(Boolean);
  const readingKey = reading.join("|");
  // The sessions themselves are in (their words and findings may still come).
  const settled = !(match.loading || (!match.data && !match.error)) && found.status !== "loading";
  const error = match.error || (found.status === "error" ? found.error : null);
  return useMemo(() => ({
    ...model, span, reading, settled, findingsReady, labelsTick,
    findings: fleet.findings || NONE, clipped: !!fleet.clipped, capped: !!match.data?.capped, error,
  }), [model, span, readingKey, settled, findingsReady, labelsTick, fleet.findings, fleet.clipped, match.data, error]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Where Triage left the findings this reads, as the flow's last column does:
// each kept finding not read yet, set aside, or put to the standards; each
// outcome's findings by session, the worst first.
export const outcomeOf = (f) => (!f.verdict ? "not_triaged" : !ASKABLE.has(f.verdict) ? "set_aside" : f.met?.length ? "met" : f.asked?.length ? "not_met" : "not_asked");
// A set of findings as the sessions they were found in, worst first, and as
// a tally by severity: what an outcome's or a type's card lists.
export function sessionsOfFindings(model, list) {
  const sessions = new Map();
  for (const f of list) {
    if (!sessions.has(f.session)) sessions.set(f.session, []);
    sessions.get(f.session).push(f);
  }
  const groups = [...sessions].map(([sid, findings]) => {
    const s = model.sessions.get(sid) || {
      id: sid, endpointId: findings[0]?.endpoint || "unknown", title: "", agent: "", firstMs: findings[0]?.at ?? null, lastMs: null,
      requests: 0, findings, worst: worstOf(findings), met: [...new Set(findings.flatMap((f) => f.met || NONE))], verified: findings.filter((f) => ASKABLE.has(f.verdict)).length,
    };
    const sorted = [...findings].sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9));
    return { s, findings: sorted, worst: worstOf(sorted), at: Math.max(...sorted.map((f) => f.at || 0)) };
  }).sort((a, b) => a.worst - b.worst || b.findings.length - a.findings.length || b.at - a.at);
  const sev = {};
  for (const f of list) sev[f.severity || "unrated"] = (sev[f.severity || "unrated"] || 0) + 1;
  return { groups, sev };
}
export function outcomesOf(model) {
  const by = new Map(OUTCOMES.map((o) => [o.id, []]));
  for (const f of model.findings) by.get(outcomeOf(f)).push(f);
  const out = [];
  for (const o of OUTCOMES) {
    const list = by.get(o.id);
    if (!list.length) continue;
    out.push({ ...o, findings: list, ...sessionsOfFindings(model, list) });
  }
  return out;
}
